import * as Location from 'expo-location';
import { Business } from '@/components/locations/types';
import { Coordinate } from '@usepepper/ui/molecules/map/types';
import { getMetersBetween } from '@/utils/distance';
import { Region } from 'react-native-maps';
import { toPercentageString } from '@/utils/percentage';
import { useCallback, useEffect, useMemo, useState } from 'react';

const MAX_YELP_RADIUS = 40000;

export const COLLAPSED_SHEET_SIZE_PERCENT = 15;
export const OPEN_SHEET_SIZE_PERCENT = 75;

export const snapPoints = [
  toPercentageString(COLLAPSED_SHEET_SIZE_PERCENT),
  toPercentageString(OPEN_SHEET_SIZE_PERCENT),
];

export const useLeadGeneratorLocation = () => {
  const [directHit, setDirectHit] = useState(false);
  // user's current location with `currentLocation` being the more accurate location
  const [lastKnownLocation, setLastKnownLocation] = useState<Coordinate | null>(
    null,
  );
  const [currentLocation, setCurrentLocation] = useState<Coordinate | null>(
    null,
  );

  const [selectedBusiness, setSelectedBusiness] = useState<
    Business | undefined
  >(undefined);

  const [listSize, setListSize] = useState(COLLAPSED_SHEET_SIZE_PERCENT);
  const [showLeadGeneratorList, setShowLeadGeneratorList] = useState(true);
  // lead generator search
  const [selectedLocationName, setSelectedLocationName] = useState<
    string | undefined
  >(undefined);

  // selected lat/long location for the map
  const [leadGeneratorLocation, setLeadGeneratorLocation] = useState<{
    latitude: number | null;
    longitude: number | null;
    radius: string | null;
  }>({
    latitude: null,
    longitude: null,
    radius: null,
  });

  // location when user drags the map
  const [dragLeadGeneratorLocation, setDragLeadGeneratorLocation] =
    useState<Coordinate | null>(null);

  // location when user zooms on the map
  const [zoomLeadGeneratorLocation, setZoomLeadGeneratorLocation] = useState<
    string | null
  >(null);

  // whether the map / search area has changed
  const [mapChanged, setMapChanged] = useState(false);

  useEffect(() => {
    const fetchLastKnownLocation = async () => {
      const location = await Location.getLastKnownPositionAsync();

      if (location) {
        setLastKnownLocation({
          latitude: location.coords.latitude,
          longitude: location.coords.longitude,
        });
      }
    };

    const fetchLocation = async () => {
      const location = await Location.getCurrentPositionAsync();

      if (location) {
        setCurrentLocation({
          latitude: location.coords.latitude,
          longitude: location.coords.longitude,
        });
      }
    };

    // Fetching the user's current location may indefinitely hang, so as a backup
    // we also fetch their last known location, which should be less accurate but
    // respond near-immediately.
    (async () => {
      await fetchLastKnownLocation();
      await fetchLocation();
    })();
  }, []);

  // set default map location to the user's current location or the last known location
  useEffect(() => {
    // prioritize the more accurate location (current location)
    if (currentLocation) {
      setLeadGeneratorLocation({
        latitude: currentLocation.latitude,
        longitude: currentLocation.longitude,
        radius: null,
      });
    } else if (lastKnownLocation) {
      setLeadGeneratorLocation({
        latitude: lastKnownLocation.latitude,
        longitude: lastKnownLocation.longitude,
        radius: null,
      });
    }
  }, [currentLocation, lastKnownLocation]);

  const getUserLocation = useCallback((): Coordinate | null => {
    if (currentLocation) {
      return {
        latitude: currentLocation.latitude,
        longitude: currentLocation.longitude,
      };
    } else if (lastKnownLocation) {
      return {
        latitude: lastKnownLocation.latitude,
        longitude: lastKnownLocation.longitude,
      };
    } else {
      return null;
    }
  }, [currentLocation, lastKnownLocation]);

  const userLocation = getUserLocation();

  const isUserLocation = useMemo(() => {
    return (
      userLocation?.latitude === leadGeneratorLocation.latitude &&
      userLocation?.longitude === leadGeneratorLocation.longitude
    );
  }, [userLocation, leadGeneratorLocation]);

  // reset map location
  const selectCurrentLocation = useCallback(() => {
    setSelectedLocationName(undefined);
    setLeadGeneratorLocation({
      latitude: userLocation?.latitude ?? null,
      longitude: userLocation?.longitude ?? null,
      radius: null,
    });
    setDragLeadGeneratorLocation(null);
    setMapChanged(false);
  }, [userLocation]);

  // update the dragged location when the user drags the map
  const onRegionChangeComplete = useCallback(
    (e: Region) => {
      if (mapChanged) {
        const centerOfMap = {
          latitude: e.latitude,
          longitude: e.longitude,
        };

        const edgeOfMap = {
          latitude: e.latitude + e.latitudeDelta / 2,
          longitude: e.longitude,
        };

        const radius = Math.min(
          getMetersBetween(
            centerOfMap.latitude,
            centerOfMap.longitude,
            edgeOfMap.latitude,
            edgeOfMap.longitude,
          ),
          MAX_YELP_RADIUS,
        );

        // Set the radius (distance from center to top edge)
        setDragLeadGeneratorLocation(centerOfMap);
        setZoomLeadGeneratorLocation(Math.round(radius).toString());
      }
    },
    [mapChanged],
  );

  // set the search location to the dragged location
  const onSearchArea = useCallback(() => {
    // Set bottom sheet to collapsed after searching area
    setListSize(COLLAPSED_SHEET_SIZE_PERCENT);

    setLeadGeneratorLocation({
      latitude: dragLeadGeneratorLocation!.latitude,
      longitude: dragLeadGeneratorLocation!.longitude,
      radius: zoomLeadGeneratorLocation,
    });
    // hide business info card when user searches a new area
    setSelectedBusiness(undefined);
    setDragLeadGeneratorLocation(null);
    setMapChanged(false);
  }, [dragLeadGeneratorLocation, zoomLeadGeneratorLocation]);

  const onMapTouch = useCallback(() => {
    // hide business info card when user drags the map
    setSelectedBusiness(undefined);
    // Collapse bottom sheet when dragging the map
    setListSize(COLLAPSED_SHEET_SIZE_PERCENT);
    setShowLeadGeneratorList(true);
  }, []);

  const onBusinessSelect = useCallback(
    (selectedBusiness: Business) => {
      // reset direct-hit so we don't hide the others
      setDirectHit(false);
      setMapChanged(false);
      setSelectedBusiness(selectedBusiness);
      // we can directly hide the lead generator list when a business is selected
      setShowLeadGeneratorList(false);
    },
    [
      setDirectHit,
      setMapChanged,
      setSelectedBusiness,
      setShowLeadGeneratorList,
    ],
  );

  const onPositionChanged = useCallback(
    (index: number) => {
      if (index <= 0) {
        setListSize(COLLAPSED_SHEET_SIZE_PERCENT);
      } else {
        setListSize(OPEN_SHEET_SIZE_PERCENT);

        // Set mapChanged when opening the bottom sheet
        // so that the "Search this area" button is hidden
        setMapChanged(false);
      }
    },
    [setListSize],
  );

  // On any pan or zoom of the map, set mapChanged to true
  // so that the "Search this area" button is shown
  const onPanDrag = useCallback(() => {
    setMapChanged(true);
  }, [setMapChanged]);

  return useMemo(
    () => ({
      directHit,
      dragLeadGeneratorLocation,
      isUserLocation,
      leadGeneratorLocation,
      listSize,
      mapChanged,
      onBusinessSelect,
      onMapTouch,
      onPanDrag,
      onPositionChanged,
      onRegionChangeComplete,
      onSearchArea,
      selectCurrentLocation,
      selectedBusiness,
      selectedLocationName,
      setDirectHit,
      setLeadGeneratorLocation,
      setListSize,
      setMapChanged,
      setSelectedBusiness,
      setSelectedLocationName,
      setShowLeadGeneratorList,
      showLeadGeneratorList,
      userLocation,
    }),
    [
      directHit,
      dragLeadGeneratorLocation,
      isUserLocation,
      leadGeneratorLocation,
      listSize,
      mapChanged,
      onBusinessSelect,
      onMapTouch,
      onPanDrag,
      onPositionChanged,
      onRegionChangeComplete,
      onSearchArea,
      selectCurrentLocation,
      selectedBusiness,
      selectedLocationName,
      setDirectHit,
      setLeadGeneratorLocation,
      setListSize,
      setMapChanged,
      setSelectedBusiness,
      setSelectedLocationName,
      setShowLeadGeneratorList,
      showLeadGeneratorList,
      userLocation,
    ],
  );
};
